import { AiStatus } from '../entity/api/ai';
import { type Brand } from '../entity/api/brand';
import {
  type CreateBrandReq,
  type CreateBrandRes,
  type GenerateBrandContextReq,
  type GenerateBrandContextRes,
  type GenerateBrandContextSocketResSchema,
  type GetByIdRes,
  type JobDataReq,
  type JobDataRes,
  type JobStatusRes,
  type ListRes,
  type SaveBrandReq,
  type SaveBrandRes,
  createBrandReqSchema,
  deleteBrandReqSchema,
  generateBrandContextReqSchema,
  jobDataReqSchema,
  saveBrandReqSchema,
} from '../entity/api/routes/brand';
import { InternalServerError } from '../error';
import { apiRequest, setURL } from './request';

// NOTE: We Store pending promises for brand generation
const pendingBrandGenerations = new Map<
  string,
  {
    resolve: (value: GenerateBrandContextSocketResSchema) => void;
    reject: (reason?: unknown) => void;
    country: string;
    timeoutId: ReturnType<typeof setTimeout>;
    jobId: string | null;
  }
>();

const DEFAULT_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

export function getBrandsList() {
  return apiRequest<ListRes>('/brand/list', 'GET', true);
}

export function getBrandById(id: string) {
  return apiRequest<GetByIdRes>(`/brand/${id}`, 'GET', true);
}

export async function createBrand(data: CreateBrandReq['body'], isFromOnboarding: boolean) {
  const req = createBrandReqSchema.parse({
    params: null,
    query: { isOnboarding: isFromOnboarding },
    body: data,
  });
  const url = setURL('/brand', req.query);
  return apiRequest<CreateBrandRes>(url, 'POST', true, req.body);
}

export async function updateBrand(id: SaveBrandReq['params']['id'], data: SaveBrandReq['body']) {
  const req = saveBrandReqSchema.parse({
    params: { id },
    query: null,
    body: data,
  });
  return apiRequest<SaveBrandRes>(`/brand/${req.params.id}`, 'PATCH', true, req.body);
}

export async function removeBrand(id: Brand['_id']) {
  const req = deleteBrandReqSchema.parse({
    params: { id },
    query: null,
    body: null,
  });
  return apiRequest<unknown>(`/brand/${req.params.id}`, 'DELETE', true);
}

// NOTE: We handle the socket event when the brand context generation is completed
export function onBrandGenerateContextUpdate(data: GenerateBrandContextSocketResSchema) {
  const { contentId, status } = data;
  const pending = pendingBrandGenerations.get(contentId);

  if (pending != null) {
    if (status === AiStatus.Success) {
      clearTimeout(pending.timeoutId);
      (data.result as Partial<Brand>).country = data.result?.country?.toLowerCase() ?? 'US';
      // Preserve the language from the API response, only fallback to country conversion if not provided
      (data.result as Partial<Brand>).language = data.result?.language?.toLowerCase() ?? 'en';
      pending.resolve(data);
      pendingBrandGenerations.delete(contentId);
    } else if (status === AiStatus.Error) {
      clearTimeout(pending.timeoutId);
      pending.reject(new InternalServerError(data.error ?? 'Brand context generation failed'));
      pendingBrandGenerations.delete(contentId);
    }
    // For 'pending' or 'cancelled' status, we keep waiting (we don't resolve or reject yet)
  }
}

export function cancelBrandGeneration(contentId: Brand['_id']) {
  const pending = pendingBrandGenerations.get(contentId);
  if (pending != null) {
    clearTimeout(pending.timeoutId);
    pending.reject(new InternalServerError('Brand context generation cancelled'));
    pendingBrandGenerations.delete(contentId);
  }
}

export function generateBrandContext(
  data: GenerateBrandContextReq['body']
): Promise<GenerateBrandContextSocketResSchema> {
  const req = generateBrandContextReqSchema.parse({
    params: null,
    query: null,
    body: data,
  });
  const url = setURL(`/brand/${req.body.contentId}/generate-context`, req.query);

  // We create a promise that will be resolved when the socket event arrives
  const socketPromise = new Promise<GenerateBrandContextSocketResSchema>(function (
    resolve,
    reject
  ) {
    // NOTE: We timeout after 3 minutes if no response
    const timeoutId = setTimeout(function () {
      const pending = pendingBrandGenerations.get(req.body.contentId);
      if (pending != null) {
        if (pending.jobId != null) {
          // eslint-disable-next-line no-use-before-define
          getBrandJobDataByJobId(pending.jobId, req.body.contentId)
            /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */
            .then(function (res) {
              if (res.result != null && res.status === AiStatus.Success) {
                const resultBrand = res.result as Partial<Brand> | null;
                const brandContext = {
                  ...resultBrand,
                  country: data.country ?? 'US',
                  // Preserve the language from the API response, fallback to request language
                  language: resultBrand?.language?.toLowerCase() ?? data.language ?? 'en',
                };
                pending.resolve({
                  contentId: req.body.contentId,
                  status: AiStatus.Success,
                  error: '',
                  result: brandContext,
                });
              } else {
                pending.reject(
                  new InternalServerError(res.error ?? 'Brand context generation failed')
                );
              }
            })
            /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */
            .catch(function (err: unknown) {
              pending.reject(err);
            })
            .finally(function () {
              pendingBrandGenerations.delete(req.body.contentId);
            });
          return; // Don't fall through to the reject below
        }

        pending.reject(
          new InternalServerError(
            `Brand context generation timed out after ${DEFAULT_TIMEOUT_MS / 1000} seconds`
          )
        );
        pendingBrandGenerations.delete(req.body.contentId);
      }
    }, DEFAULT_TIMEOUT_MS);

    // NOTE: Store the promise handlers and timeout ID together
    pendingBrandGenerations.set(req.body.contentId, {
      resolve: function (value) {
        clearTimeout(timeoutId);
        resolve(value);
      },
      reject: function (reason: unknown) {
        clearTimeout(timeoutId);
        reject(reason instanceof Error ? reason : new Error(String(reason)));
      },
      timeoutId,
      country: data.country ?? 'US',
      jobId: null,
    });
  });

  // We launch the HTTP request (don't await it)
  apiRequest<GenerateBrandContextRes>(url, 'POST', true, req.body)
    .then(function (res) {
      const pending = pendingBrandGenerations.get(req.body.contentId);
      if (pending != null) {
        pending.jobId = res.jobId as string | null;
      }
    })
    .catch(function (err) {
      // If the HTTP request fails, we reject the socket promise and clear the timeout
      const pending = pendingBrandGenerations.get(req.body.contentId);
      if (pending != null) {
        clearTimeout(pending.timeoutId);
        pending.reject(err);
        pendingBrandGenerations.delete(req.body.contentId);
      }
    });

  // We return the promise that will resolve when the socket event arrives
  return socketPromise;
}

export function generateBrandContextFromOnboarding(
  data: GenerateBrandContextReq['body']
): Promise<GenerateBrandContextRes> {
  const req = generateBrandContextReqSchema.parse({
    params: null,
    query: null,
    body: data,
  });
  const url = setURL(`/brand/${req.body.contentId}/generate-context`, req.query);

  return apiRequest<GenerateBrandContextRes>(url, 'POST', true, req.body);
}

export function getBrandJobStatus(brandId: string): Promise<JobStatusRes> {
  return apiRequest<JobStatusRes>(`/brand/${brandId}/job-status`, 'GET', true);
}

/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */
export function getBrandJobDataByJobId(
  jobId: JobDataReq['params']['jobId'],
  contentId: string
): Promise<JobDataRes> {
  const req = jobDataReqSchema.parse({
    params: { jobId },
    query: null,
    body: null,
  });
  const url = setURL(`/brand/${contentId}/job-data/${req.params.jobId}`);
  return apiRequest<JobDataRes>(url, 'GET', true);
}
/* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */

export function pollBrandJobStatus(
  brandId: string,
  onStatusUpdate: (status: JobStatusRes) => void,
  intervalMs: number = 30000
): () => void {
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let isCancelled = false;

  const poll = function (): void {
    if (isCancelled) {
      return;
    }

    getBrandJobStatus(brandId)
      .then(function (apiResponse: JobStatusRes): void {
        if (isCancelled) {
          return;
        }
        onStatusUpdate(apiResponse);

        // Stop polling if job is completed, errored, or cancelled
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const jobStatus = apiResponse.status as AiStatus;
        const shouldStopPolling =
          jobStatus === AiStatus.Success ||
          jobStatus === AiStatus.Error ||
          jobStatus === AiStatus.Cancelled;

        if (shouldStopPolling && intervalId != null) {
          clearInterval(intervalId);
          intervalId = null;
        }
      })
      .catch(function (err: unknown): void {
        if (isCancelled) {
          return;
        }
        // On error, we still notify the caller but don't stop polling
        // The caller can decide what to do
        // eslint-disable-next-line no-console
        console.error('Error polling job status:', err);
      });
  };

  // Start polling immediately
  poll();

  // Then poll at the specified interval
  intervalId = setInterval(poll, intervalMs);

  // Return a cancel function
  return function () {
    isCancelled = true;
    if (intervalId != null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
}
